import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useWorkflow } from '../WorkflowContext';
import { useTheme } from '../ThemeContext';
import Topbar from './Topbar';
import CommandPalette from './CommandPalette';
import OsMenu from './OsMenu';

type Group = { title: string; items: { to: string; ico: string; label: string }[] };

// CRM's own pages — revealed via the "Open CRM Workspace" button.
const FRONT_GROUPS: Group[] = [
  { title: 'Workspace', items: [{ to: '/', ico: '⌂', label: 'Home' }] },
  {
    title: 'Records',
    items: [
      { to: '/customers', ico: '📇', label: 'Customer CRM' },
      { to: '/catalog', ico: '🗂', label: 'Materials & Dies' },
      { to: '/clients', ico: '◎', label: 'Clients' },
      { to: '/aliases', ico: '⇄', label: 'Confirm the spelling' },
      { to: '/vendors', ico: '◐', label: 'Vendors' },
      { to: '/accounts', ico: '◉', label: 'All Accounts' },
    ],
  },
  {
    title: 'Sales',
    items: [
      { to: '/web-leads', ico: '◭', label: 'Web Leads' },
      { to: '/pipeline', ico: '◫', label: 'Opportunity Pipeline' },
      { to: '/quote-builder', ico: '⌁', label: 'Quote Builder' },
      { to: '/quote-engine', ico: '✎', label: 'Quote Engine (OS)' },
      { to: '/quotes', ico: '❑', label: 'Quote Registry' },
      { to: '/follow-ups', ico: '◷', label: 'Follow-Ups' },
      { to: '/reorders', ico: '↻', label: 'Reorder Intelligence' },
      { to: '/comms-log', ico: '✉', label: 'Communication Log' },
      { to: '/sales-calendar', ico: '▦', label: 'Sales Calendar' },
      { to: '/sales-ai', ico: '✦', label: 'AI Sales Assistant' },
    ],
  },
  {
    title: 'MFX OS — Order Chain',
    items: [
      { to: '/os', ico: '⬢', label: 'OS Command' },
      { to: '/os/pos', ico: '⇪', label: 'PO Inbox' },
      { to: '/os/sos', ico: '▣', label: 'Sales Orders' },
      { to: '/os/tickets', ico: '▤', label: 'Job Tickets' },
      { to: '/os/items', ico: '◈', label: 'Items & Blueprints' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/requests', ico: '☰', label: 'Requests' },
      { to: '/intake', ico: '⬡', label: 'Intake Center' },
      { to: '/prepress', ico: '◰', label: 'Prepress' },
      { to: '/tasks', ico: '▤', label: 'Task Board' },
      { to: '/calendar', ico: '▦', label: 'Shared Calendar' },
    ],
  },
];

const BACKEND_GROUPS: Group[] = [
  { title: 'Command', items: [{ to: '/dashboard', ico: '◧', label: 'Command Dashboard' }] },
  { title: 'Intelligence', items: [{ to: '/ai', ico: '✦', label: 'AI Command Room' }] },
  {
    title: 'Blueprint',
    items: [
      { to: '/manual', ico: '◳', label: 'Operating Manual' },
      { to: '/kpis', ico: '◆', label: 'KPI Dashboard' },
      { to: '/roadmap', ico: '◇', label: 'Implementation Roadmap' },
    ],
  },
  {
    title: 'System',
    items: [
      { to: '/architecture', ico: '⊞', label: 'Architecture' },
      { to: '/audit', ico: '◈', label: 'Sync + Audit Log' },
    ],
  },
];

const BACKEND_PATHS = ['/dashboard', '/ai', '/manual', '/kpis', '/roadmap', '/architecture', '/audit'];

export default function AppShell({ children }: { children: ReactNode }) {
  const { user, demo, signOut } = useAuth();
  const { theme, toggle } = useTheme();
  const wf = useWorkflow();
  const loc = useLocation();
  const nav = useNavigate();
  const inBackend = BACKEND_PATHS.includes(loc.pathname);
  const isHome = loc.pathname === '/';
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [crmMode, setCrmMode] = useState<boolean>(() => {
    try { return localStorage.getItem('mfx-crm-nav') === 'crm'; } catch { return false; }
  });
  const setMode = (crm: boolean) => { setCrmMode(crm); try { localStorage.setItem('mfx-crm-nav', crm ? 'crm' : 'os'); } catch { /* ignore */ } };

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((o) => !o); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const name = user?.displayName || (demo ? 'Demo viewer' : 'Signed in');
  const email = user?.email || (demo ? 'offline · bundled data' : '');
  const initials = (name || 'U').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  const isActive = (to: string) =>
    loc.pathname === to || (to === '/clients' && loc.pathname === '/client');

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">M</div>
          <div>
            <div className="brand-name">MFX CRM</div>
            <div className="brand-sub">{inBackend ? 'Staff Backend' : 'Microflex Film'}</div>
          </div>
        </div>

        {inBackend ? (
          <>
            <button className="demo-btn" onClick={() => nav('/')}><span className="nav-ico">←</span><span>Back to Workspace</span></button>
            {BACKEND_GROUPS.map((g) => (
              <div key={g.title}>
                <div className="nav-group">{g.title}</div>
                {g.items.map((n) => (
                  <NavLink key={n.to} to={n.to} className={`nav-item${isActive(n.to) ? ' active' : ''}`}>
                    <span className="nav-ico">{n.ico}</span><span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
            <div className="nav-group">Demo</div>
            <div className="demo-controls">
              <button className="demo-btn run" onClick={wf.runDemo}><span className="nav-ico">▸</span><span>Run Full Automation</span></button>
              <button className="demo-btn" onClick={wf.reset}><span className="nav-ico">↺</span><span>Reset Demo</span></button>
            </div>
          </>
        ) : crmMode ? (
          <>
            <button className="demo-btn run" onClick={() => setMode(false)}><span className="nav-ico">◍</span><span>Microflex OS Menu</span></button>
            {FRONT_GROUPS.map((g) => (
              <div key={g.title}>
                <div className="nav-group">{g.title}</div>
                {g.items.map((n) => (
                  <NavLink key={n.to} to={n.to} className={`nav-item${isActive(n.to) ? ' active' : ''}`}>
                    <span className="nav-ico">{n.ico}</span><span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </>
        ) : (
          <>
            <button className="demo-btn run" onClick={() => setMode(true)}><span className="nav-ico">◎</span><span>Open CRM Workspace</span></button>
            <OsMenu />
          </>
        )}

        <div className="sidebar-foot">
          <button className="theme-toggle" onClick={toggle}>
            <span className="ico">{theme === 'light' ? '☾' : '☀'}</span>
            <span>{theme === 'light' ? 'Dark mode' : 'Light mode'}</span>
          </button>
          <div className="user-chip">
            <div className="avatar">{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
              <div style={{ fontSize: 10, color: 'var(--tx3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
            </div>
          </div>
          <button className="signout" onClick={signOut}>Sign out</button>
        </div>
      </aside>

      <main className="main">
        {!isHome && <Topbar onSearch={() => setPaletteOpen(true)} />}
        <div className="page-anim" key={loc.pathname + loc.search}>{children}</div>
      </main>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
